import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMXN, openBase64Pdf } from "../lib/api";
import { MapPin, UtensilsCrossed, Loader2, FileText } from "lucide-react";

function WhatsAppIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <path fill="#25D366" d="M16.003 3.2C9.0944 3.2 3.4995 8.795 3.4995 15.703c0 2.207.586 4.299 1.612 6.106L3.2 28.8l7.166-1.864a12.434 12.434 0 0 0 5.637 1.346h.005c6.907 0 12.503-5.595 12.503-12.503 0-6.908-5.596-12.578-12.508-12.578Zm0 22.79h-.005a10.36 10.36 0 0 1-5.272-1.443l-.378-.224-4.247 1.104 1.134-4.135-.246-.391a10.354 10.354 0 0 1-1.587-5.508c0-5.737 4.673-10.408 10.41-10.408 2.78 0 5.392 1.083 7.357 3.05a10.342 10.342 0 0 1 3.043 7.36c-.001 5.737-4.673 10.595-10.41 10.595Zm5.706-7.741c-.312-.157-1.852-.913-2.139-1.017-.286-.105-.494-.157-.703.157-.208.314-.806 1.017-.989 1.226-.182.21-.364.235-.677.078-.312-.157-1.32-.486-2.514-1.55-.93-.83-1.557-1.853-1.74-2.166-.182-.314-.02-.483.137-.64.141-.14.313-.366.47-.549.156-.183.208-.314.313-.523.105-.21.052-.392-.026-.55-.078-.156-.703-1.695-.963-2.32-.254-.61-.512-.527-.703-.537a13.6 13.6 0 0 0-.6-.012c-.208 0-.547.078-.832.392-.286.314-1.092 1.067-1.092 2.601s1.118 3.018 1.274 3.227c.156.21 2.201 3.361 5.336 4.713.745.321 1.327.513 1.78.657.748.238 1.428.205 1.966.124.6-.09 1.852-.757 2.114-1.488.26-.732.26-1.36.182-1.488-.078-.13-.286-.21-.598-.366Z" />
    </svg>
  );
}

export default function MenuPage() {
  const { identifier } = useParams();
  const [biz, setBiz] = useState(null);
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/directory/menu/${identifier}`)
      .then(async (menuRes) => {
        setMenu(menuRes.data);
        const bizRes = await api.get(`/directory/business/${menuRes.data.business_id}`);
        setBiz(bizRes.data);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [identifier]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
      </div>
    );
  }

  if (notFound || !biz) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <UtensilsCrossed className="w-12 h-12 text-muted-foreground opacity-30" />
        <h1 className="font-display text-3xl">Menú no encontrado</h1>
        <p className="text-muted-foreground text-sm">Este enlace no está disponible.</p>
        <Link to="/" className="text-[#7C3AED] text-sm font-medium hover:underline">Ir a gpsofertas.com</Link>
      </div>
    );
  }

  const wa = biz.whatsapp || "";
  const waDigits = wa.replace(/\D/g, "");
  const whatsappUrl = wa
    ? `https://wa.me/52${waDigits}?text=${encodeURIComponent(`Hola, vi su menú en OFERTAS y quiero más información`)}`
    : null;

  const categories = (menu?.categories || []).filter((c) => c.items?.length > 0);
  const hasAnyImage = categories.some((c) => c.items.some((it) => it.image_base64));

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* ── Header ── */}
      <div className="bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Brand link */}
          <Link to="/" className="inline-block text-white/60 hover:text-white text-[11px] font-semibold tracking-widest uppercase mb-5 transition-colors">
            gpsofertas.com
          </Link>

          {/* Business identity */}
          <div className="flex items-start gap-4">
            {biz.logo_base64 ? (
              <img
                src={biz.logo_base64}
                alt={biz.business_name}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-white/20 shadow-lg shrink-0"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                <span className="text-3xl font-display font-bold text-white/80 select-none">
                  {biz.business_name?.[0]?.toUpperCase()}
                </span>
              </div>
            )}

            <div className="flex-1 min-w-0 pt-1">
              <div className="text-white/60 text-[11px] font-bold uppercase tracking-widest">{biz.giro}</div>
              <h1 className="font-display text-2xl sm:text-3xl leading-tight mt-0.5">{biz.business_name}</h1>
              <div className="flex items-start gap-1.5 mt-2 text-white/70 text-xs">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{biz.address} · CP {biz.postal_code}</span>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB857] active:scale-95 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-all shadow-lg shadow-black/20"
                data-testid="menu-page-whatsapp-btn"
              >
                <WhatsAppIcon className="w-5 h-5" /> Contactar por WhatsApp
              </a>
            )}
            {menu?.menu_pdf_base64 && (
              <button
                type="button"
                onClick={() => openBase64Pdf(menu.menu_pdf_base64)}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 active:scale-95 border border-white/30 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-all"
                data-testid="menu-page-pdf-btn"
              >
                <FileText className="w-5 h-5" /> Ver menú PDF
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Menu body ── */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {categories.length === 0 ? (
          !menu?.menu_pdf_base64 && (
            <div className="text-center py-16 text-muted-foreground">
              <UtensilsCrossed className="w-10 h-10 mx-auto mb-3 opacity-25" />
              <p className="text-sm">Este negocio aún no ha publicado su menú.</p>
            </div>
          )
        ) : (
          <div className="space-y-8">
            {categories.map((cat) => (
              <section key={cat.id}>
                {/* Category label */}
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="font-bold text-[11px] uppercase tracking-[0.2em] text-[#7C3AED] shrink-0">{cat.name}</h2>
                  <div className="flex-1 h-px bg-[#EDE9FE]" />
                </div>

                {/* Items */}
                <div className="space-y-px">
                  {cat.items.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-start gap-3 sm:gap-4 py-3.5 border-b border-border last:border-0 transition-opacity ${!item.available ? "opacity-45" : ""}`}
                      data-testid={`menu-page-item-${item.id}`}
                    >
                      {/* Item image */}
                      {(hasAnyImage) && (
                        <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-zinc-100 border border-border">
                          {item.image_base64 ? (
                            <img src={item.image_base64} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full" />
                          )}
                        </div>
                      )}

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="font-semibold text-sm sm:text-base">{item.name}</span>
                          {!item.available && (
                            <span className="text-[10px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-medium">
                              No disponible
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-relaxed">{item.description}</p>
                        )}
                      </div>

                      {/* Price */}
                      {item.price != null && (
                        <span className="font-bold text-sm sm:text-base text-[#5B21B6] shrink-0 mt-0.5">
                          {formatMXN(item.price)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-border text-center space-y-1.5">
          <Link
            to={`/directorio/${biz.postal_code}`}
            className="text-xs text-[#7C3AED] hover:underline font-medium"
          >
            Ver más negocios cerca · CP {biz.postal_code}
          </Link>
          <div className="text-[10px] text-muted-foreground/50 uppercase tracking-widest">gpsofertas.com</div>
        </div>
      </div>
    </div>
  );
}
